import { Text } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type {
  ContributingEntityCommitmentDto,
  ContributingEntityDetailDto,
  ContributingEntityQualificationResponseDto,
  EspdResponseDto,
} from "../../../../autogen/bff-api";
import { PortalWizardShell } from "../../../../common/wizard/PortalWizardShell";
import { usePortalStepLabel, usePortalStepShortLabel } from "../../../../common/wizard/usePortalStepLabel";
import { useWizardStepNavigation } from "../../../../common/wizard/useWizardStepNavigation";
import type { WizardStepPath } from "../../../../common/wizard/wizardStepPaths";
import {
  contributionStepVisibility,
  contributionWizardModel,
  type ContributionStep,
} from "./contributionWizardSteps";
import { ContributionCommitmentStep } from "./steps/ContributionCommitmentStep";
import { ContributionExclusionStep } from "./steps/ContributionExclusionStep";
import { ContributionIntroStep } from "./steps/ContributionIntroStep";
import { ContributionQualificationStep } from "./steps/ContributionQualificationStep";
import { ContributionReviewStep } from "./steps/ContributionReviewStep";
import { ContributionSupplierStep } from "./steps/ContributionSupplierStep";

export type ContributionAudience = "contributor" | "lead";

const ignoreChange = () => undefined;

interface Props {
  detail: ContributingEntityDetailDto;
  espd: EspdResponseDto;
  qualificationResponses: ContributingEntityQualificationResponseDto[];
  commitment: ContributingEntityCommitmentDto;
  onEspdChange?: (next: EspdResponseDto) => void;
  onQualificationsChange?: (next: ContributingEntityQualificationResponseDto[]) => void;
  onCommitmentChange?: (next: ContributingEntityCommitmentDto) => void;
  readOnly: boolean;
  audience: ContributionAudience;
  stepPaths: Record<ContributionStep, WizardStepPath>;
  backTo: string;
  backLabel: string;
  title: string;
  headingAside?: ReactNode;
  underSubtitle?: ReactNode;
  isSaving?: boolean;
  isSubmitting?: boolean;
  onSubmit?: () => void;
}

export const ContributionWizard = ({
  detail,
  espd,
  qualificationResponses,
  commitment,
  onEspdChange,
  onQualificationsChange,
  onCommitmentChange,
  readOnly,
  audience,
  stepPaths,
  backTo,
  backLabel,
  title,
  headingAside,
  underSubtitle,
  isSaving = false,
  isSubmitting = false,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const visibility = contributionStepVisibility(detail, audience === "contributor");
  const stepLabel = usePortalStepLabel();
  const stepShortLabel = usePortalStepShortLabel();
  const { currentStep, navigateToStep, navPropsFor } = useWizardStepNavigation({
    model: contributionWizardModel,
    visibility,
    stepPaths,
    stepLabel,
  });

  if (!currentStep) return <Text>{t("Loading")}</Text>;

  const context = detail.context;
  const renderStep = (): ReactNode => {
    switch (currentStep) {
      case "intro":
        return (
          <ContributionIntroStep
            context={context}
            entityName={detail.entity.name}
            {...navPropsFor("intro")}
          />
        );
      case "supplier":
        return (
          <ContributionSupplierStep
            name={detail.entity.name}
            organizationNumber={detail.entity.organizationNumber}
            role={detail.entity.role}
            groupRole={detail.entity.groupRole}
            groupName={detail.context.groupName}
            otherParticipants={detail.context.otherParticipants}
            espd={espd}
            onChange={onEspdChange ?? ignoreChange}
            readOnly={readOnly}
            audience={audience}
            {...navPropsFor("supplier")}
          />
        );
      case "exclusion":
        return (
          <ContributionExclusionStep
            criteria={context.exclusionCriteria ?? []}
            espd={espd}
            onChange={onEspdChange ?? ignoreChange}
            readOnly={readOnly}
            isSaving={isSaving}
            {...navPropsFor("exclusion")}
          />
        );
      case "qualification":
        return (
          <ContributionQualificationStep
            selectionCriteria={context.selectionCriteria ?? []}
            responses={qualificationResponses}
            onChange={onQualificationsChange ?? ignoreChange}
            readOnly={readOnly}
            {...navPropsFor("qualification")}
          />
        );
      case "commitment":
        return (
          <ContributionCommitmentStep
            leadSupplierName={context.leadSupplierName}
            commitment={commitment}
            onChange={onCommitmentChange ?? ignoreChange}
            readOnly={readOnly}
            audience={audience}
            {...navPropsFor("commitment")}
          />
        );
      case "review":
        return onSubmit ? (
          <ContributionReviewStep
            detail={detail}
            espd={espd}
            onChange={onEspdChange ?? ignoreChange}
            readOnly={readOnly}
            isSubmitting={isSubmitting}
            isSaving={isSaving}
            onSubmit={onSubmit}
            previousStep={navPropsFor("review").previousStep}
          />
        ) : null;
    }
  };

  return (
    <PortalWizardShell
      backTo={backTo}
      backLabel={backLabel}
      title={title}
      subtitle={context.eventTitle}
      headingAside={headingAside}
      underSubtitle={underSubtitle}
      steps={contributionWizardModel.visibleSteps(visibility)}
      currentStep={currentStep}
      stepLabel={stepLabel}
      stepShortLabel={stepShortLabel}
      navigateToStep={navigateToStep}
    >
      {renderStep()}
    </PortalWizardShell>
  );
};
